/* ==========================================================================
   Nakoda Silver - soft navigation (v5.8, 2026-09-25)

   Why this exists: every link used to load a whole new page, which destroys the
   <audio> element. The music player saved and restored its place, but there was
   always a gap, and Safari (iPad / iPhone) will not play sound on a freshly
   loaded page until the visitor taps again - so the music stopped or restarted
   on every page (Rahul, 2026-09-25).

   Now a tap on an internal link fetches the next page and swaps only the
   content between the header and the footer (#sn-main). The header, footer,
   cart drawer and music player never unload, so the song simply carries on.

   Anything unusual falls back to a normal page load: other sites, new tabs,
   downloads, cart / checkout / account, form posts, or a failed fetch.
   Search engines are unaffected - every URL still serves a full page.
   ========================================================================== */
(function () {
  "use strict";
  if (!window.fetch || !window.DOMParser || !history.pushState) return;
  var main = document.getElementById("sn-main");
  if (!main) return;

  // Paths that must always be a real page load.
  var HARD = /^\/(cart|checkout|checkouts|account|apps|tools|password|challenge|services|admin|a\/|cdn\/)/;
  var FILE = /\.(pdf|jpe?g|png|gif|webp|svg|mp3|mp4|mov|zip|xml|json|txt)$/i;

  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  history.replaceState(stateWith({ y: window.scrollY }), "", location.href);

  function stateWith(extra) {
    var s = {}, k, cur = history.state || {};
    for (k in cur) s[k] = cur[k];
    for (k in extra) s[k] = extra[k];
    s.sn = 1;
    return s;
  }

  // ---- page scripts --------------------------------------------------------
  // scroll.js (reveal-on-scroll, parallax, card videos, gallery) sets itself up
  // once per page load. Re-run it after each swap, and remove the listeners the
  // previous run added so they do not pile up.
  var scrollTag = document.querySelector('script[src*="/scroll.js"]');
  var scrollCode = null, tracked = [];

  function reveal() {
    [].forEach.call(document.querySelectorAll(".rv,.rw"), function (el) { el.classList.add("in"); });
  }
  function track(run) {
    var wA = window.addEventListener, dA = document.addEventListener;
    window.addEventListener = function (t, f, o) { tracked.push([window, t, f, o]); return wA.call(window, t, f, o); };
    document.addEventListener = function (t, f, o) { tracked.push([document, t, f, o]); return dA.call(document, t, f, o); };
    try { run(); } finally { delete window.addEventListener; delete document.addEventListener; }
  }
  function rerunScroll() {
    tracked.forEach(function (x) { x[0].removeEventListener(x[1], x[2], x[3]); });
    tracked = [];
    [].forEach.call(document.querySelectorAll(".scrollprog"), function (n) { n.parentNode.removeChild(n); });
    var go = function () {
      try { track(function () { (0, eval)(scrollCode); }); } catch (e) { reveal(); }
    };
    if (scrollCode !== null) return go();
    if (!scrollTag) return reveal();
    fetch(scrollTag.src, { credentials: "same-origin" })
      .then(function (r) { return r.text(); })
      .then(function (t) { scrollCode = t; go(); })
      .catch(reveal);
  }

  // Shop filter buttons (All / Earrings / ...) are wired by cart.js on first load
  // only. Wire the ones in the new content the same way.
  function applyHash() {
    var h = (location.hash || "").replace("#", "").toLowerCase();
    if (!h) return;
    [].forEach.call(main.querySelectorAll("[data-filter]"), function (b) {
      var f = (b.getAttribute("data-filter") || "").toLowerCase().replace(/\s+/g, "-");
      if (f === h) b.click();
    });
  }
  function initFilters() {
    var filters = main.querySelectorAll("[data-filter]");
    [].forEach.call(filters, function (b) {
      b.addEventListener("click", function () {
        var f = b.getAttribute("data-filter");
        [].forEach.call(filters, function (x) { x.classList.toggle("on", x === b); });
        [].forEach.call(main.querySelectorAll("[data-cat]"), function (card) {
          card.style.display = f === "all" || card.getAttribute("data-cat") === f ? "" : "none";
        });
      });
    });
    if (filters.length) setTimeout(applyHash, 0);
  }

  // Scripts inside the new content (e.g. the product gallery) do not run when
  // inserted with innerHTML; replace each with a fresh copy so they do.
  function runScripts(root) {
    [].forEach.call(root.querySelectorAll("script"), function (old) {
      if (old.type && !/javascript|module/i.test(old.type)) return;   // JSON-LD etc.
      var s = document.createElement("script");
      [].forEach.call(old.attributes, function (a) { s.setAttribute(a.name, a.value); });
      if (s.src) s.async = false;
      s.text = old.text;
      old.parentNode.replaceChild(s, old);
    });
  }

  function syncHead(doc) {
    document.title = doc.title;
    ['meta[name="description"]', 'link[rel="canonical"]', 'meta[property="og:title"]',
     'meta[property="og:description"]', 'meta[property="og:image"]'].forEach(function (sel) {
      var mine = document.head.querySelector(sel), theirs = doc.head.querySelector(sel);
      if (mine && theirs) mine.parentNode.replaceChild(theirs.cloneNode(true), mine);
      else if (mine && !theirs) mine.parentNode.removeChild(mine);
      else if (!mine && theirs) document.head.appendChild(theirs.cloneNode(true));
    });
  }

  function pageView() {
    try { if (window.ShopifyAnalytics && ShopifyAnalytics.lib && ShopifyAnalytics.lib.page) ShopifyAnalytics.lib.page(); } catch (e) {}
    try { if (window.fbq) window.fbq("track", "PageView"); } catch (e) {}
    try { if (window.gtag) window.gtag("event", "page_view", { page_location: location.href, page_title: document.title }); } catch (e) {}
  }

  // ---- navigation ----------------------------------------------------------
  var seq = 0;
  function go(url, push, y) {
    var my = ++seq;
    document.documentElement.classList.add("sn-loading");
    fetch(url, { credentials: "same-origin" })
      .then(function (r) {
        var ct = r.headers.get("content-type") || "";
        if (ct.indexOf("text/html") < 0) throw new Error("not html");
        return r.text().then(function (h) { return { html: h, url: r.redirected ? r.url : url }; });
      })
      .then(function (res) {
        if (my !== seq) return;                       // a newer tap won
        var doc = new DOMParser().parseFromString(res.html, "text/html");
        var next = doc.getElementById("sn-main");
        if (!next) throw new Error("no main");        // different layout: load it for real
        if (push) {
          history.replaceState(stateWith({ y: window.scrollY }), "", location.href);
          history.pushState({ sn: 1, y: 0 }, "", res.url);
        }
        syncHead(doc);
        main.innerHTML = next.innerHTML;
        var nav = document.querySelector(".hdr .nav"), nnav = doc.querySelector(".hdr .nav");
        if (nav && nnav) nav.innerHTML = nnav.innerHTML;   // keep the "current page" underline right
        var hdr = document.querySelector(".hdr");
        if (hdr) hdr.classList.remove("open");             // close the mobile menu
        runScripts(main);
        var hash = push && res.url.indexOf("#") > -1 ? document.getElementById(res.url.split("#")[1]) : null;
        if (hash) hash.scrollIntoView(); else window.scrollTo(0, y || 0);
        rerunScroll();
        initFilters();
        document.dispatchEvent(new CustomEvent("ns:navigated", { detail: { url: location.href } }));
        pageView();
      })
      .catch(function () { if (my === seq) location.href = url; })
      .then(function () { if (my === seq) document.documentElement.classList.remove("sn-loading"); });
  }

  document.addEventListener("click", function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest ? e.target.closest("a[href]") : null;
    if (!a || a.hasAttribute("download") || a.hasAttribute("data-no-soft")) return;
    if (a.target && a.target !== "_self") return;
    if (a.origin !== location.origin || HARD.test(a.pathname) || FILE.test(a.pathname)) return;
    if (a.pathname === location.pathname && a.search === location.search && a.hash) return;   // same-page anchor
    e.preventDefault();
    go(a.href, true, 0);
  });

  window.addEventListener("popstate", function (e) {
    if (!e.state || !e.state.sn) return;              // plain #hash changes
    go(location.href, false, e.state.y || 0);
  });

  // Remember where she was on this page, so Back returns to the same spot.
  var saveT = null;
  window.addEventListener("scroll", function () {
    clearTimeout(saveT);
    saveT = setTimeout(function () { history.replaceState(stateWith({ y: window.scrollY }), "", location.href); }, 150);
  }, { passive: true });
  window.addEventListener("hashchange", applyHash);
})();
